import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingCart, X, AlertTriangle } from "lucide-react";
import DeliverySection from "../components/checkout/DeliverySection";
import CouponSection from "../components/checkout/CouponSection";
import PaymentSection from "../components/checkout/PaymentSection";
import OrderSummary from "../components/checkout/OrderSummary";
import CheckoutLoadingScreen from "../components/checkout/CheckoutLoadingScreen";
import MessageModal from "../components/modal/message/MessageModal";
import { useCreateOrder } from "../hooks/useCreateOrder";
import { useApi } from "../hooks/useApi";
import { useOrderStatus } from "../contexts/OrderStatusContext";
import { COMPANY_KEY } from "../config/company";
import { FEATURES } from "../config/features";
import { useOnlinePayment } from "../hooks/useOnlinePayment";
import {
  ONLINE_PAYMENT_STATUS,
  RETRYABLE_STATUSES,
  isOnlinePayableMethod,
  saveLastOnlinePayment,
} from "../lib/onlinePayment";


const formatBRL = (n) =>
  (typeof n === "number" ? n : Number(n || 0)).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  });

const onlyDigits = (v) => String(v || "").replace(/\D/g, "");

// Tempo máximo na tela de carregamento antes de exibir a página
const PAGE_READY_TIMEOUT_MS = 8000;

// GET /companies/{code}/delivery → boolean (aceita também formatos envelopados)
const isDeliveryAvailable = (data) =>
  !(
    data === false ||
    data?.data === false ||
    data?.data?.[0]?.delivery === false
  );

export default function CheckoutPage() {
  const navigate = useNavigate();
  const {
    createOrder,
    sendOnlyToWhatsApp,
    isLoading: isOrderLoading,
  } = useCreateOrder();
  const api = useApi();

  // Hook do contexto de status de pedido
  const { openOrderStatusModal } = useOrderStatus();

  // Estados do carrinho (recuperados do localStorage)
  const [cart, setCart] = useState([]);
  const [subtotalPrice, setSubtotalPrice] = useState(0);

  // Estados de entrega
  const [deliveryType, setDeliveryType] = useState("pickup");
  // null = ainda não verificado (botão Delivery visível; valida no clique)
  const [deliveryEnabled, setDeliveryEnabled] = useState(null);
  // false enquanto as configurações da loja carregam (CheckoutLoadingScreen)
  const [isPageReady, setIsPageReady] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const [address, setAddress] = useState({
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    uf: "",
    estado: "",
  });
  const [freight, setFreight] = useState(null);

  // Estados de cupom
  const [couponData, setCouponData] = useState(null);
  const [discountValue, setDiscountValue] = useState(0);

  // Estados de pagamento
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentMethodSelected, setPaymentMethodSelected] = useState("");
  const [payments, setPayments] = useState([]);

  // 💳 Pagamento online (rota unificada /online-payments; provedor definido no painel)
  const {
    availability: onlinePayment,
    checkAvailability: checkOnlinePayment,
    startCheckout: startOnlineCheckout,
  } = useOnlinePayment();
  const [payOnline, setPayOnline] = useState(true);
  // Falha ao gerar o link (UNKNOWN/CREATION_FAILED/erro): permite nova tentativa
  const [onlineRetry, setOnlineRetry] = useState({
    isOpen: false,
    orderCode: "",
    message: "",
  });

  // Estado do modal de mensagem
  const [messageModal, setMessageModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Estado de loading
  const [uiBusy, setUiBusy] = useState(false);

  // 🎯 Estado para controlar processamento do pedido (previne duplo clique)
  const [isProcessing, setIsProcessing] = useState(false);

  // Estados do modal PIX
  const [isPixModalOpen, setIsPixModalOpen] = useState(false);
  const [pixAmount, setPixAmount] = useState(0);
  const [pixPayload, setPixPayload] = useState("");
  const [pixQrDataUrl, setPixQrDataUrl] = useState("");
  const [pixTxid, setPixTxid] = useState("");
  const [pixCountdown, setPixCountdown] = useState(0);
  const pixTextAreaRef = useRef(null);

  // Estado do modal de confirmação de pagamento
  const [isPaymentConfirmModalOpen, setIsPaymentConfirmModalOpen] =
    useState(false);

  // Função para calcular o total do carrinho
  const getTotalPrice = () => {
    return cart.reduce(
      (total, item) => total + item.product_price * item.quantity,
      0
    );
  };

  // 🎯 Validação reativa do formulário: devolve o primeiro motivo que impede
  // finalizar (exibido no resumo) ou "" quando está tudo certo
  const pendingReason = useMemo(() => {
    // 1. Verifica campos obrigatórios básicos
    if (!customerInfo.name || !customerInfo.name.trim()) {
      return "Informe seu nome.";
    }

    const phone = onlyDigits(customerInfo.phone);
    if (!phone || phone.length < 10) {
      return "Informe um telefone/WhatsApp válido com DDD.";
    }

    // 2. Se for delivery, verifica endereço e frete
    if (deliveryType === "delivery") {
      if (
        onlyDigits(address.cep).length !== 8 ||
        !address.logradouro?.trim() ||
        !address.numero?.trim() ||
        !address.bairro?.trim()
      ) {
        return "Preencha o endereço de entrega: CEP, número, logradouro e bairro.";
      }

      // O frete é calculado automaticamente ao completar o CEP (DeliverySection)
      if (!freight) {
        return 'Aguardando o cálculo do frete. Se não aparecer, toque em "Calcular Frete".';
      }

      if (!freight.available) {
        return "Não entregamos neste CEP. Escolha Retirada ou outro endereço.";
      }
    }

    // 3. Verifica forma de pagamento
    if (!paymentMethod) {
      return "Escolha a forma de pagamento.";
    }

    // 4. Verifica se há itens no carrinho
    if (!cart || cart.length === 0) {
      return "Seu carrinho está vazio.";
    }

    return "";
  }, [
    customerInfo.name,
    customerInfo.phone,
    deliveryType,
    address.cep,
    address.logradouro,
    address.numero,
    address.bairro,
    freight,
    paymentMethod,
    cart,
  ]);

  const isFormValid = !pendingReason;

  // Callback para quando pedido for criado com sucesso
  const handleOrderCreated = (codigo, orderAddress) => {
    console.log("🔔 Pedido criado com código:", codigo);

    // Salva no localStorage
    localStorage.setItem("pedido.codigo", String(codigo));
    localStorage.setItem("pedido.status", "0");
    localStorage.setItem("pedido.delivery", "0");

    // Formata endereço se disponível
    if (orderAddress && orderAddress.logradouro) {
      const formattedAddress = `${orderAddress.logradouro}, Nº ${
        orderAddress.numero
      }${
        orderAddress.complemento ? " (" + orderAddress.complemento + ")" : ""
      }`;
      localStorage.setItem("pedido.address", formattedAddress);
    }
  };

  // Callback para limpar carrinho após pedido criado
  const handleClearCart = () => {
    setCart([]);
    localStorage.removeItem("bonfim_cart");
    localStorage.removeItem("bonfim_checkout_data");
    localStorage.removeItem("bonfim_products_tracking");
    localStorage.removeItem("bonfim_products_tracking_detailed");
    console.log("✅ Carrinho e dados do checkout limpos");
  };

  // Função para limpar TUDO do localStorage relacionado ao pedido e carrinho
  const handleClearAllOrderData = () => {
    // Limpa dados do carrinho
    setCart([]);
    localStorage.removeItem("bonfim_cart");
    localStorage.removeItem("bonfim_checkout_data");
    localStorage.removeItem("bonfim_products_tracking");
    localStorage.removeItem("bonfim_products_tracking_detailed");

    // Limpa dados temporários do pedido (usado para WhatsApp)
    localStorage.removeItem("pedido.data");

    // NOTA: Não remove pedido.codigo, pedido.status, pedido.delivery, pedido.address
    // pois esses são necessários para exibir o OrderStatusLine na home

    console.log(
      "✅ Carrinho e dados temporários do pedido completamente limpos"
    );
  };

  // 🎯 Scroll para o topo ao entrar na página de checkout
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Carrega dados do carrinho do localStorage
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem("bonfim_cart");
      const savedCheckoutData = localStorage.getItem("bonfim_checkout_data");

      if (!savedCart) {
        // Se não há carrinho, redireciona para home
        navigate("/");
        return;
      }

      const cartData = JSON.parse(savedCart);
      setCart(cartData);

      // Calcula subtotal
      const total = cartData.reduce((acc, item) => {
        return acc + (item.product_price || 0) * (item.quantity || 1);
      }, 0);
      setSubtotalPrice(total);

      // Carrega dados do checkout se existirem
      if (savedCheckoutData) {
        const checkoutData = JSON.parse(savedCheckoutData);
        if (checkoutData.customerInfo)
          setCustomerInfo(checkoutData.customerInfo);
        if (checkoutData.address) setAddress(checkoutData.address);
        if (checkoutData.deliveryType)
          setDeliveryType(checkoutData.deliveryType);
      }
    } catch (error) {
      console.error("Erro ao carregar dados do carrinho:", error);
      navigate("/");
    }
  }, [navigate]);

  // Salva progresso no localStorage
  useEffect(() => {
    const checkoutData = {
      customerInfo,
      address,
      deliveryType,
      freight,
      couponData,
      paymentMethod,
      paymentMethodSelected,
    };
    localStorage.setItem("bonfim_checkout_data", JSON.stringify(checkoutData));
  }, [
    customerInfo,
    address,
    deliveryType,
    freight,
    couponData,
    paymentMethod,
    paymentMethodSelected,
  ]);

  // Configurações da loja que mudam as opções da página. Enquanto não respondem,
  // a página fica no CheckoutLoadingScreen, para que botões indisponíveis já
  // apareçam ocultos.
  useEffect(() => {
    let cancelled = false;

    // Delivery (sem modal): se indisponível, oculta o botão e fixa Retirada
    const checkDelivery = api
      .get(`/companies/${COMPANY_KEY}/delivery`)
      .then((data) => {
        if (cancelled) return;
        const available = isDeliveryAvailable(data);
        setDeliveryEnabled(available);
        if (!available) setDeliveryType("pickup");
      })
      .catch((e) => {
        // Mantém o botão visível; a validação acontece no clique
        console.error("⚠️ Erro ao consultar disponibilidade do delivery:", e);
      });

    // Pagamento online ativo? (o hook nunca lança erro)
    const checkPayment = FEATURES.ONLINE_PAYMENT
      ? checkOnlinePayment()
      : Promise.resolve();

    // Segurança: se a API demorar, libera a página mesmo assim
    let timeoutId;
    const timeout = new Promise((resolve) => {
      timeoutId = setTimeout(resolve, PAGE_READY_TIMEOUT_MS);
    });

    Promise.race([Promise.allSettled([checkDelivery, checkPayment]), timeout])
      .then(() => {
        if (!cancelled) setIsPageReady(true);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [api, checkOnlinePayment]);

  // Contador do PIX (15 minutos = 900 segundos)
  useEffect(() => {
    if (!isPixModalOpen) return;
    setPixCountdown(900); // 15 minutos
    const interval = setInterval(() => {
      setPixCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isPixModalOpen]);

  // Calcula total com frete e desconto
  const totalPrice = useMemo(() => {
    const freightValue =
      deliveryType === "delivery" && freight?.available
        ? Number(freight.value || 0)
        : 0;
    const discount = Number(discountValue || 0);
    const subtotal = Number(subtotalPrice || 0);
    return Math.max(0, subtotal + freightValue - discount);
  }, [subtotalPrice, deliveryType, freight, discountValue]);

  const handleBackToHome = () => {
    navigate("/");
  };

  const showMessage = (title, message, type = "info") => {
    setMessageModal({ isOpen: true, title, message, type });
  };

  const closeMessage = () => {
    setMessageModal({ isOpen: false, title: "", message: "", type: "info" });
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleCopyPix = () => {
    if (pixTextAreaRef.current) {
      pixTextAreaRef.current.select();
      document.execCommand("copy");
      showMessage(
        "Código Copiado!",
        "O código PIX foi copiado para a área de transferência.",
        "success"
      );
    }
  };

  // Função para abrir modal PIX
  const handleOpenPixModal = async (orderCode) => {
    console.log("[handleOpenPixModal] Recebido orderCode:", orderCode);
    const amount = Number(totalPrice || 0);

    try {
      const pixPayload = {
        amount: amount,
        description: `Pedido #${orderCode}`,
        name: customerInfo.name,
        contact: onlyDigits(customerInfo.phone),
        order_code: orderCode,
        pay_method: "pix",
      };

      console.log(
        "[handleOpenPixModal] Enviando payload para /pix/create:",
        pixPayload
      );

      const data = await api.post("/pix/create", pixPayload);

      console.log("[handleOpenPixModal] PIX GERADO:", JSON.stringify(data));

      const payload = data?.payload || "";
      const qrUrl = data?.qrcodeUrl || "";
      const txid = data?.txid || "";

      setPixAmount(amount);
      setPixPayload(payload);
      setPixQrDataUrl(qrUrl);
      setPixTxid(txid);
      setIsPixModalOpen(true);
    } catch (e) {
      console.error(
        "[handleOpenPixModal] Erro ao gerar QRCode Pix pela API:",
        e
      );
      showMessage(
        "Erro ao gerar PIX",
        "Não foi possível gerar o QR Code Pix. Tente novamente.",
        "error"
      );
    }
  };

  // O pedido será pago online? (flag + integração ativa + Pix/cartão + escolha do cliente)
  const shouldPayOnline =
    FEATURES.ONLINE_PAYMENT &&
    onlinePayment.available &&
    payOnline &&
    isOnlinePayableMethod(paymentMethod, paymentMethodSelected);

  /**
   * Gera o pagamento online do pedido já registrado e redireciona o cliente.
   * O valor é calculado pelo back; o front nunca marca o pedido como pago.
   */
  const handleStartOnlinePayment = async (orderCode, newAttempt = false) => {
    setUiBusy(true);
    try {
      const payment = await startOnlineCheckout(orderCode, { newAttempt });
      saveLastOnlinePayment(payment);

      if (
        payment?.status === ONLINE_PAYMENT_STATUS.PENDING &&
        payment.checkoutUrl
      ) {
        // Pedido já registrado: limpa a sacola e segue para o provedor
        handleClearCart();
        setOnlineRetry({ isOpen: false, orderCode: "", message: "" });
        window.location.assign(payment.checkoutUrl);
        return;
      }

      if (RETRYABLE_STATUSES.includes(payment?.status)) {
        setOnlineRetry({
          isOpen: true,
          orderCode,
          message:
            payment.message ||
            "Não foi possível gerar o link de pagamento. Tente novamente.",
        });
        return;
      }

      // CREATING, CONFIRMED ou REVIEW: a página de retorno acompanha o status
      handleClearCart();
      setOnlineRetry({ isOpen: false, orderCode: "", message: "" });
      navigate(
        `/pagamento/retorno?paymentId=${encodeURIComponent(payment.paymentId)}`
      );
    } catch (error) {
      setOnlineRetry({
        isOpen: true,
        orderCode,
        message:
          error?.data?.message ||
          "Não foi possível iniciar o pagamento online. Tente novamente.",
      });
    } finally {
      setUiBusy(false);
    }
  };

  // Desiste do pagamento online: o pedido segue registrado para pagar depois
  const handlePayLater = () => {
    setOnlineRetry({ isOpen: false, orderCode: "", message: "" });
    handleClearCart();
    setIsPaymentConfirmModalOpen(true);
  };

  // Validação de delivery
  const fetchDeliveryValidate = async () => {
    try {
      const data = await api.get(`/companies/${COMPANY_KEY}/delivery`);

      if (!isDeliveryAvailable(data)) {
        setDeliveryEnabled(false);
        showMessage(
          "Delivery Indisponível",
          "No momento estamos aceitando apenas pedidos para retirada. Obrigado pela compreensão!",
          "warning"
        );
        setDeliveryType("pickup");
        return false;
      } else {
        setDeliveryEnabled(true);
        return true;
      }
    } catch (e) {
      console.error("Erro ao validar delivery:", e);
      showMessage(
        "Erro de Conexão",
        "Não foi possível validar o delivery no momento. Por favor, tente novamente.",
        "error"
      );
      setDeliveryType("pickup");
      return false;
    }
  };

  const handleSendOrder = async () => {
    // 🛡️ Previne múltiplos cliques
    if (isProcessing) {
      console.log("⚠️ Pedido já está sendo processado. Aguarde...");
      return;
    }

    // 🛡️ Validação final (redundante, mas garante consistência)
    if (!isFormValid) {
      console.log("⚠️ Formulário inválido. Verifique os campos.");
      return;
    }

    // 🚀 Inicia processamento
    setIsProcessing(true);

    try {
      // Validações originais mantidas para mensagens específicas
      if (!customerInfo.name || !customerInfo.phone) {
        showMessage(
          "Dados Incompletos",
          "Por favor, preencha seu nome e telefone para continuar.",
          "warning"
        );
        return;
      }

      if (deliveryType === "delivery") {
        if (
          !address.cep ||
          !address.logradouro ||
          !address.numero ||
          !address.bairro
        ) {
          showMessage(
            "Endereço Incompleto",
            "Por favor, preencha todos os campos do endereço de entrega.",
            "warning"
          );
          return;
        }
        if (!freight || !freight.available) {
          showMessage(
            "Calculando Frete",
            "Por favor, aguarde o cálculo do frete ou verifique se o CEP está correto.",
            "info"
          );
          return;
        }
      }

      if (!paymentMethod) {
        showMessage(
          "Forma de Pagamento",
          "Por favor, selecione uma forma de pagamento para continuar.",
          "warning"
        );
        return;
      }

      // Prepara dados do frete
      const freightData =
        deliveryType === "delivery" && freight?.available ? freight : null;

      // Obtém o código do cupom se houver
      const couponCode = couponData?.valid ? couponData.code : null;

      // Cria o pedido
      const result = await createOrder(
        customerInfo.name,
        onlyDigits(customerInfo.phone),
        deliveryType,
        address,
        paymentMethod,
        cart,
        getTotalPrice,
        freightData,
        couponCode
      );

      // Clique repetido ignorado: o envio em andamento cuida do restante
      if (result === null) return;

      // Se pedido criado com sucesso
      if (result && result.codigo) {
        const codigo = String(result.codigo);

        // Salva dados do pedido
        const pedidoData = {
          codigo,
          customerInfo: {
            name: customerInfo.name,
            phone: onlyDigits(customerInfo.phone),
          },
          deliveryType,
          address,
          cart: JSON.parse(JSON.stringify(cart)),
          totalPrice: getTotalPrice(),
          paymentMethodSelected,
          freightData,
        };
        localStorage.setItem("pedido.data", JSON.stringify(pedidoData));

        // Notifica callbacks
        handleOrderCreated(codigo, address);

        // Verifica se é pagamento PIX
        const isPixPayment = (paymentMethodSelected || "")
          .toUpperCase()
          .includes("PIX");

        console.log(
          "[handleSendOrder] Método de pagamento:",
          paymentMethodSelected
        );
        console.log("[handleSendOrder] É PIX?", isPixPayment);

        if (shouldPayOnline) {
          // 💳 Pagamento online: gera o link no provedor e redireciona
          await handleStartOnlinePayment(codigo);
        } else if (isPixPayment) {
          // Se for PIX, abre modal do PIX
          console.log(
            "[handleSendOrder] Abrindo modal PIX com código:",
            codigo
          );

          // Ativa loading profissional
          setUiBusy(true);
          await handleOpenPixModal(codigo);
          setUiBusy(false);
        } else {
          // Para outros pagamentos, limpa carrinho e abre modal de confirmação
          handleClearCart();

          console.log(
            "[handleSendOrder] Abrindo modal de confirmação para pedido:",
            codigo
          );

          // Abre modal de confirmação de pagamento
          setIsPaymentConfirmModalOpen(true);
        }

        return result;
      } else {
        showMessage(
          "Erro ao Criar Pedido",
          "Não foi possível criar seu pedido. Por favor, tente novamente.",
          "error"
        );
      }
    } catch (error) {
      console.error("❌ Erro ao processar pedido:", error);
      showMessage(
        "Erro ao Processar Pedido",
        error.message ||
          "Ocorreu um erro inesperado. Por favor, tente novamente.",
        "error"
      );
    } finally {
      // ✅ Finaliza processamento
      setIsProcessing(false);
    }
  };

  if (!isPageReady) {
    return <CheckoutLoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 animate-in fade-in duration-300">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-gray-900 transition flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Voltar</span>
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-brand-primary flex-shrink-0" />
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">
                Finalizar Pedido
              </h1>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs sm:text-sm text-gray-600">Total</p>
              <p className="text-base sm:text-xl font-bold text-brand-primary whitespace-nowrap">
                {formatBRL(totalPrice)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Seção de Entrega */}
            <DeliverySection
              deliveryType={deliveryType}
              setDeliveryType={setDeliveryType}
              customerInfo={customerInfo}
              setCustomerInfo={setCustomerInfo}
              address={address}
              setAddress={setAddress}
              freight={freight}
              setFreight={setFreight}
              validateDelivery={fetchDeliveryValidate}
              deliveryEnabled={deliveryEnabled}
              setUiBusy={setUiBusy}
              showMessage={showMessage}
            />

            {/* Seção de Cupom */}
            <CouponSection
              subtotalPrice={subtotalPrice}
              couponData={couponData}
              setCouponData={setCouponData}
              discountValue={discountValue}
              setDiscountValue={setDiscountValue}
            />

            {/* Seção de Pagamento */}
            <PaymentSection
              payments={payments}
              setPayments={setPayments}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              paymentMethodSelected={paymentMethodSelected}
              setPaymentMethodSelected={setPaymentMethodSelected}
              onlinePayment={FEATURES.ONLINE_PAYMENT ? onlinePayment : null}
              payOnline={payOnline}
              setPayOnline={setPayOnline}
            />
          </div>

          {/* Coluna Lateral - Resumo */}
          <div className="lg:col-span-1">
            <OrderSummary
              cart={cart}
              freight={
                deliveryType === "delivery" && freight?.available
                  ? Number(freight.value || 0)
                  : 0
              }
              discountValue={discountValue}
              couponData={couponData}
              handleSendOrder={handleSendOrder}
              isOrderLoading={isOrderLoading}
              isFormValid={isFormValid}
              pendingReason={pendingReason}
              isProcessing={isProcessing}
            />
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {uiBusy && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <img
            src="https://media.giphy.com/media/y1ZBcOGOOtlpC/giphy.gif"
            alt="Carregando..."
            className="w-24 h-24 rounded-xl shadow-lg"
          />
        </div>
      )}

      {/* Modal PIX (QR Code + copia-e-cola + contador) */}
      {isPixModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Pagamento via Pix
                </h2>
                <p className="text-gray-600 text-sm">
                  Escaneie o QR Code ou use o código copia-e-cola para pagar.
                </p>
              </div>
              <button
                onClick={() => {
                  setIsPixModalOpen(false);
                  setIsPaymentConfirmModalOpen(true);
                }}
                className="bg-gray-100 hover:bg-gray-200 rounded-full p-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-col items-center gap-3">
              {pixQrDataUrl ? (
                <img
                  src={pixQrDataUrl}
                  alt="QR Code Pix"
                  className="w-60 h-60"
                />
              ) : (
                <div className="py-10 text-gray-500 text-sm">
                  Gerando QR Code...
                </div>
              )}
              <p className="text-lg font-semibold text-gray-800">
                Total: {formatBRL(pixAmount)}
              </p>
              <p
                className={`text-sm font-medium ${
                  pixCountdown > 0 ? "text-emerald-700" : "text-red-600"
                }`}
              >
                {pixCountdown > 0
                  ? `Tempo restante: ${formatCountdown(pixCountdown)}`
                  : "Tempo expirado. Gere um novo Pix se precisar."}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código Pix copia-e-cola
              </label>
              <textarea
                ref={pixTextAreaRef}
                readOnly
                defaultValue={pixPayload}
                className="w-full border border-gray-300 rounded-xl p-2 text-xs break-all bg-gray-50"
                rows={4}
              />

              <button
                type="button"
                onClick={handleCopyPix}
                className="mt-2 text-sm px-3 py-2 rounded-xl bg-gray-800 text-white hover:bg-gray-900"
              >
                Copiar código Pix
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  // Limpa carrinho antes de abrir modal de confirmação
                  handleClearCart();
                  setIsPixModalOpen(false);
                  setIsPaymentConfirmModalOpen(true);
                }}
                className="w-full sm:w-auto px-4 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold transition"
              >
                Já Paguei
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de falha ao gerar o pagamento online */}
      {onlineRetry.isOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 space-y-4 shadow-float">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-brand-accent/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-brand-600" />
              </div>
              <h2 className="text-lg font-serif font-bold text-cocoa">
                Pagamento online não iniciado
              </h2>
            </div>
            <p className="text-cocoa-muted text-sm">{onlineRetry.message}</p>
            <p className="text-cocoa-muted text-xs">
              Seu pedido #{onlineRetry.orderCode} já foi registrado.
            </p>
            <div className="space-y-3">
              <button
                type="button"
                disabled={uiBusy}
                onClick={() =>
                  handleStartOnlinePayment(onlineRetry.orderCode, true)
                }
                className="w-full min-h-[44px] px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Tentar novamente
              </button>
              <button
                type="button"
                disabled={uiBusy}
                onClick={handlePayLater}
                className="w-full min-h-[44px] px-4 py-2 rounded-xl border border-brand-600/40 text-cocoa font-semibold hover:bg-cream transition disabled:opacity-50"
              >
                Pagar depois
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Pagamento */}
      {isPaymentConfirmModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 space-y-4">
            <h2 className="text-xl font-bold text-gray-800">
              Pagamento confirmado?
            </h2>
            <p className="text-gray-600 text-sm">
              Após confirmar o pagamento, você pode acompanhar o pedido ou
              enviar os dados diretamente para o WhatsApp.
            </p>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => {
                  // Recupera o código do pedido do localStorage
                  const pedidoCodigo = localStorage.getItem("pedido.codigo");

                  console.log(
                    "🔔 Redirecionando para home e abrindo modal de status para pedido:",
                    pedidoCodigo
                  );

                  if (pedidoCodigo && pedidoCodigo !== "0") {
                    // Limpa carrinho PRIMEIRO
                    handleClearCart();

                    // Limpa dados temporários do pedido
                    localStorage.removeItem("pedido.data");

                    // Reset completo dos estados do formulário
                    setCustomerInfo({ name: "", phone: "", email: "" });
                    setAddress({
                      cep: "",
                      logradouro: "",
                      numero: "",
                      complemento: "",
                      bairro: "",
                      uf: "",
                      estado: "",
                    });
                    setDeliveryType("pickup");
                    setFreight(null);
                    setCouponData(null);
                    setDiscountValue(0);
                    setPaymentMethod("");
                    setPaymentMethodSelected("");

                    // Fecha o modal de confirmação de pagamento
                    setIsPaymentConfirmModalOpen(false);

                    // Redireciona para home
                    navigate("/");

                    // Aguarda navegação completar e abre modal de status
                    setTimeout(() => {
                      openOrderStatusModal(pedidoCodigo);
                    }, 100);
                  } else {
                    console.error("❌ Código do pedido não encontrado");
                    showMessage(
                      "Erro",
                      "Não foi possível recuperar o código do pedido.",
                      "error"
                    );
                  }
                }}
                className="w-full px-4 py-2 rounded-xl bg-brand-primary text-white font-semibold hover:bg-brand-primary-dark transition"
              >
                Acompanhar pedido
              </button>

              <button
                type="button"
                onClick={async () => {
                  try {
                    // Recupera dados do pedido salvos no localStorage
                    const pedidoDataStr = localStorage.getItem("pedido.data");

                    if (!pedidoDataStr) {
                      console.error(
                        "❌ Dados do pedido não encontrados no localStorage"
                      );
                      showMessage(
                        "Erro",
                        "Não foi possível recuperar os dados do pedido.",
                        "error"
                      );
                      return;
                    }

                    const pedidoData = JSON.parse(pedidoDataStr);

                    // Envia para WhatsApp com os dados salvos
                    await sendOnlyToWhatsApp(
                      pedidoData.customerInfo.name,
                      pedidoData.customerInfo.phone,
                      pedidoData.deliveryType,
                      pedidoData.address,
                      pedidoData.cart,
                      () => pedidoData.totalPrice,
                      pedidoData.paymentMethodSelected,
                      pedidoData.freightData
                    );

                    // Limpa carrinho
                    handleClearCart();

                    // Limpa dados temporários do pedido
                    localStorage.removeItem("pedido.data");

                    // Reset completo dos estados do formulário
                    setCustomerInfo({ name: "", phone: "", email: "" });
                    setAddress({
                      cep: "",
                      logradouro: "",
                      numero: "",
                      complemento: "",
                      bairro: "",
                      uf: "",
                      estado: "",
                    });
                    setDeliveryType("pickup");
                    setFreight(null);
                    setCouponData(null);
                    setDiscountValue(0);
                    setPaymentMethod("");
                    setPaymentMethodSelected("");

                    // Fecha modal
                    setIsPaymentConfirmModalOpen(false);

                    // Mostra mensagem de sucesso
                    showMessage(
                      "Enviado!",
                      "Pedido enviado para WhatsApp com sucesso!",
                      "success"
                    );

                    // Aguarda um pouco e redireciona para home
                    setTimeout(() => {
                      navigate("/");
                    }, 1500);
                  } catch (error) {
                    console.error("Erro ao enviar para WhatsApp:", error);
                    showMessage(
                      "Erro",
                      "Não foi possível enviar para WhatsApp. Tente novamente.",
                      "error"
                    );
                  }
                }}
                className="w-full px-4 py-2 rounded-xl bg-green-500 text-white font-semibold hover:bg-green-600 transition"
              >
                Enviar pedido no WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Mensagem */}
      <MessageModal
        isOpen={messageModal.isOpen}
        title={messageModal.title}
        message={messageModal.message}
        type={messageModal.type}
        onClose={closeMessage}
      />
    </div>
  );
}
